import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import FixDropdownOption, { FixDropdownOptionItem } from "./FixDropdownOption";
import styles from "./FixDropdown.module.scss";
import { Icon } from "@/elements-react/fix-icon";

interface FixDropdownProps {
  items: FixDropdownOptionItem[];
  onSelect?: (value: string | number, index: number) => boolean | void;
  isSearchable?: boolean;
  onSearch?: (value: string) => void;
  controlled?: boolean;
  placeholder?: string;
  label?: string;
}

const FixDropdown: React.FC<FixDropdownProps> = ({
  items,
  onSelect = () => true,
  isSearchable = true,
  onSearch,
  controlled = false,
  placeholder,
  label,
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [selected, setSelected] = useState<FixDropdownOptionItem | undefined>(undefined);
  const [searchTerm, setSearchTerm] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);
  const optionsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    const handleClick = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [isOpen]);

  const selectedItem = useMemo(() => {
    if (controlled) {
      return items.find((item) => item.selected);
    }
    return selected ?? items.find((item) => item.selected);
  }, [controlled, items, selected]);

  const handleSelect = useCallback(
    (item: FixDropdownOptionItem, index: number) => {
      setSelected(item);
      setIsOpen(false);
      onSelect?.(item.value, index);
    },
    [onSelect],
  );

  const handleSearch = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.currentTarget.value;
      setSearchTerm(value);
      onSearch?.(value);
    },
    [onSearch],
  );

  const filteredOptions = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return items;
    return items.filter((item) => item.label.toLowerCase().includes(term) || item.value.toString().toLowerCase().includes(term));
  }, [items, searchTerm]);

  return (
      <div className={classNames(styles.dropdownField)}>
          {label && <label className={styles.label}>{label}</label>}
    <div className={classNames(styles.fixDropdownContainer, { [styles.opened]: isOpen })} ref={containerRef}>
      <div className={styles.selectedValue} onClick={() => setIsOpen((v) => !v)} tabIndex={0} role="button" aria-haspopup="listbox" aria-expanded={isOpen}>
        <div className={classNames(styles.selectLabel)}>
          {selectedItem ? (
            <div className={classNames(styles.selected)}>
              { selectedItem.icon &&
                <span className={classNames(styles.icon)}>
                  {selectedItem.icon}
                </span>
              }

              <span className={classNames(styles.label)}>
                {selectedItem.label}
              </span>
            </div>
          ) : (
            <div className={classNames(styles.notSelected)}>
              <span className={classNames(styles.label)}>
                {placeholder ? placeholder : t("dropdown.select")}
              </span>
            </div>
          )}
        </div>

        <span className={classNames(styles.dropdownArrow, { [styles.open]: isOpen })}>
          <Icon name="chevron-down" />
        </span>
      </div>

      {/*
        FIXME: Add translates for keys:
        // `dropdown.search`
        // `dropdown.noOptions`
      */}
      {isOpen && (
        <div className={styles.options} ref={optionsRef} role="listbox">
          {isSearchable && <div className={styles.searchInputContainer}>
            <input className={styles.searchInput} type="text" value={searchTerm} onChange={handleSearch} placeholder={t("dropdown.search", "Type to search")} />
          </div>}

          {filteredOptions.length === 0 && <div className={styles.noOptions}>{t("dropdown.noOptions", "Nothing found")}</div>}

          {filteredOptions.map((item, idx) => (
            <div key={item.value} onClick={() => handleSelect(item, idx)}>
              <FixDropdownOption label={item.label} value={item.value} selected={selectedItem?.value === item.value} icon={item.icon} />
            </div>
          ))}
        </div>
      )}
    </div>
      </div>
  );
};

export default FixDropdown;
